import {
    ClaimApi,
    CoreFetch,
    GuarantorApi,
    ListSupportClaimsRequest,
    ListSupportGuarantorsRequest,
    ListSupportPatientsRequest,
    ListSupportPayersRequest,
    ListSupportProvidersRequest,
    PatientApi,
    PayerApi,
    ProviderApi,
    SupportClaim,
    SupportGuarantor,
    SupportPatient,
    SupportPayer,
    SupportProvider,
} from '@enter/core';
import _ from 'lodash';
import * as mobx from 'mobx';
import { ClaimsStore } from '../../stores/claims.store';
import {
    accountNumberFilter,
    betweenDayBoundsFilter,
    equalsFilter,
    firstAndLastFilter,
    isDate,
    isDateTime,
    isNumber,
    isUuid,
    likeFilter,
} from './utils';

export type ClaimSearchMode = 'patient' | 'payer' | 'guarantor' | 'provider';

export interface ISearchResult {
    claims?: SupportClaim[];
    patients?: SupportPatient[];
    payers?: SupportPayer[];
    guarantors?: SupportGuarantor[];
    providers?: SupportProvider[];
}

export class ClaimSearchPresenter {
    @mobx.observable.ref public claims: SupportClaim[] = [];
    @mobx.observable.ref public patients: SupportPatient[] = [];

    public findClaim(claimId: string): SupportClaim | undefined {
        return _.find(this.claims, { id: claimId });
    }

    public async searchForMode(mode: ClaimSearchMode, term: string, treatUUIDAsClaimId: boolean = false): Promise<ISearchResult> {
        let searchResults: ISearchResult = {};
        if (mode === 'patient') {
            searchResults = await this.searchModePatient(term, treatUUIDAsClaimId);
        } else if (mode === 'guarantor') {
            searchResults = await this.searchModeGuarantor(term);
        } else if (mode === 'provider') {
            searchResults = await this.searchModeProvider(term);
        } else if (mode === 'payer') {
            searchResults = await this.searchModePayer(term);
        }
        return searchResults;
    }

    private searchModePatient = async (term: string, treatUUIDAsClaimId: boolean = false): Promise<ISearchResult> => {
        const searchTerm = _.trim(term);
        let claims: SupportClaim[] = [];
        //let patients: SupportPatient[] = [];

        // find claims, then find related patients
        // claims = await this.fetchClaims(this.searchTerm2ClaimRequests(searchTerm, true));
        // patients = await this.fetchPatients(this.claims2PatientRequests(claims));
        claims = await this.combinedSearchForSupportClaimsByTerm(searchTerm, treatUUIDAsClaimId);

        return {
            claims,
        };
    };
    private searchModeGuarantor = async (term: string): Promise<ISearchResult> => {
        const searchTerm = _.trim(term);
        let claims: SupportClaim[] = [];
        let guarantors: SupportGuarantor[] = [];

        const claimRequests = this.searchTerm2ClaimRequests(searchTerm);
        if (!_.isEmpty(claimRequests)) {
            // find claims, then find related guarantors
            claims = await this.combinedSearchForSupportClaimsByTerm(term);
            guarantors = await this.fetchGuarantors(this.claims2GuarantorRequests(claims));
        } else {
            // no claims matched, find matching guarantors and related claims
            guarantors = await this.fetchGuarantors(this.searchTerm2GuarantorRequests(searchTerm));
            claims = await this.fetchClaims(this.guarantors2ClaimRequests(guarantors));
        }

        return {
            claims,
            guarantors,
        };
    };

    private searchModePayer = async (term: string): Promise<ISearchResult> => {
        const searchTerm = _.trim(term);
        const payers: SupportPayer[] = await this.fetchPayers(this.searchTerm2PayerRequests(searchTerm));
        return {
            payers,
        };
    };

    private searchModeProvider = async (term: string): Promise<ISearchResult> => {
        const searchTerm = _.trim(term);
        const providers: SupportProvider[] = await this.fetchProviders(this.searchTerm2ProviderRequests(searchTerm));
        return {
            providers,
        };
    };

    private readonly searchTerm2ClaimRequests = (
        term: string,
        searchPatientNames: boolean = false,
    ): ListSupportClaimsRequest[] => {
        let requests: ListSupportClaimsRequest[] = [];
        if (isDateTime(term) || isDate(term)) {
            const filter = isDateTime(term) ? betweenDayBoundsFilter(term) : equalsFilter(term);
            requests.push({ filterPaymentDate: filter });
            requests.push({ filterFromDos: filter });
        } else if (isNumber(term) && _.size(term) > 5) {
            requests.push({ filterInvoiceNumber: equalsFilter(term) });
            requests.push({ filterAccountNumber: equalsFilter(term) });
        } else if (isUuid(term)) {
            const filter = equalsFilter(term);
            requests.push({ filterId: filter });
        } else if (searchPatientNames) {
            const filters = [...firstAndLastFilter(term), ...accountNumberFilter(term)];
            requests = [...requests, ...filters];
        }
        return requests;
    };

    private readonly searchTerm2GuarantorRequests = (term: string): ListSupportGuarantorsRequest[] => {
        let requests: ListSupportGuarantorsRequest[] = [];
        if (isUuid(term)) {
            const filter = equalsFilter(term);
            requests.push({ filterId: filter });
        } else {
            const filters = firstAndLastFilter(term);
            requests = [...requests, ...filters];
        }
        return requests;
    };

    private readonly searchTerm2PayerRequests = (term: string): ListSupportPayersRequest[] => {
        const requests: ListSupportPayersRequest[] = [];
        if (isUuid(term)) {
            const filter = equalsFilter(term);
            requests.push({ filterId: filter });
        } else {
            requests.push({ filterName: likeFilter(term) });
        }
        return requests;
    };

    private readonly searchTerm2ProviderRequests = (term: string): ListSupportProvidersRequest[] => {
        let requests: ListSupportProvidersRequest[] = [];
        if (isUuid(term)) {
            const filter = equalsFilter(term);
            requests.push({ filterId: filter });
        } else {
            const filters = firstAndLastFilter(term);
            requests = [...requests, ...filters];
        }
        return requests;
    };

    private readonly claims2GuarantorRequests = (claims: SupportClaim[]): ListSupportGuarantorsRequest[] => {
        const requests: ListSupportGuarantorsRequest[] = [];
        if (!_.isEmpty(claims)) {
            const guarantorIds = _.uniq(_.map(claims, 'guarantorId'));
            const filters = _.compact(_.flatMap(guarantorIds, guarantorId => equalsFilter(guarantorId)));
            requests.push({
                filterId: filters,
            });
        }
        return requests;
    };

    private readonly claims2PatientRequests = (claims: SupportClaim[]): ListSupportPatientsRequest[] => {
        const requests = [];
        // NB: only consider unique patient names
        const firsts = _.uniq(_.compact(_.map(claims, claim => claim.patientFirst)));
        if (!_.isEmpty(firsts)) {
            requests.push({ filterFirst: likeFilter(firsts) });
        }
        const lasts = _.uniq(_.compact(_.map(claims, claim => claim.patientLast)));
        if (!_.isEmpty(lasts)) {
            requests.push({ filterLast: likeFilter(lasts) });
        }
        return requests;
    };

    private readonly guarantors2ClaimRequests = (guarantors: SupportGuarantor[]): ListSupportClaimsRequest[] => {
        const requests: ListSupportClaimsRequest[] = [];
        if (!_.isEmpty(guarantors)) {
            const guarantorIds = _.uniq(_.map(guarantors, 'id'));
            const filters = _.compact(_.flatMap(guarantorIds, guarantorId => equalsFilter(guarantorId)));
            requests.push({
                filterGuarantorId: filters,
            });
        }
        return requests;
    };

    private readonly fetchClaims = async (requests: ListSupportClaimsRequest[]): Promise<SupportClaim[]> => {
        const claims = await Promise.all(requests.map(req => CoreFetch<ClaimApi>(ClaimApi).listSupportClaims(req)));
        return _.chain(claims)
            .flatten()
            .map(claim => ClaimsStore.updateClaimStatus(claim))
            .compact()
            .uniqBy('id')
            .value();
    };

    private readonly combinedSearchForSupportClaimsByTerm = async (query: string, treatUUIDAsClaimId: boolean = false): Promise<SupportClaim[]> => {
        const trimmedQuery = _.trim(query);
        
        if (treatUUIDAsClaimId && isUuid(trimmedQuery)) {
            try {
                const claims = await ClaimsStore.getById(trimmedQuery);
                return claims;
            } catch (error) {
                console.warn('Direct claim lookup failed, falling back to search API', error);
            }
        }
        
        const listClaims = CoreFetch<ClaimApi>(ClaimApi).listSearchSupportClaims({search: [trimmedQuery]});
        const claims = await Promise.all([listClaims]);
        return _.chain(claims)
            .flatten()
            .map(claim => ClaimsStore.updateClaimStatus(claim))
            .compact()
            .uniqBy('id')
            .value();
    };
    private readonly fetchGuarantors = async (
        requests: ListSupportGuarantorsRequest[],
    ): Promise<SupportGuarantor[]> => {
        const guarantors = await Promise.all(
            requests.map(req => CoreFetch<GuarantorApi>(GuarantorApi).listSupportGuarantors(req)),
        );
        return _.uniqBy(_.compact(_.flatten(guarantors)), 'id');
    };
    private readonly fetchPatients = async (requests: ListSupportPatientsRequest[]): Promise<SupportPatient[]> => {
        const patients = await Promise.all(
            requests.map(req => CoreFetch<PatientApi>(PatientApi).listSupportPatients({ ...req, limit: 100 })),
        );
        return _.uniqBy(_.compact(_.flatten(patients)), 'id');
    };
    private readonly fetchPayers = async (requests: ListSupportPayersRequest[]): Promise<SupportPayer[]> => {
        const payers = await Promise.all(requests.map(req => CoreFetch<PayerApi>(PayerApi).listSupportPayers(req)));
        return _.uniqBy(_.compact(_.flatten(payers)), 'id');
    };
    private readonly fetchProviders = async (requests: ListSupportProvidersRequest[]): Promise<SupportProvider[]> => {
        const providers = await Promise.all(
            requests.map(req => CoreFetch<ProviderApi>(ProviderApi).listSupportProviders(req)),
        );
        return _.uniqBy(_.compact(_.flatten(providers)), 'id');
    };
}
